import type { ReactNode } from "react";

/**
 * A physical screen (kiosk / monitor) baked into a STILL PHOTO, with a
 * cinematic push-in zoom, that hosts a DOM overlay UI landing EXACTLY on the
 * screen regardless of the browser window's size or aspect ratio.
 *
 * WHY THIS EXISTS: the previous approach used a full-viewport
 * `background-size:cover` layer + a hand-tuned `translate(Xvw,Yvh)` offset on
 * the DOM overlay. `cover` crops the image DIFFERENTLY depending on the
 * viewport's aspect ratio, so the "fixed" vw/vh offset only lined up at the
 * one window size it was tuned against — it visibly drifted (misaligned
 * screen) at any other window size/shape. That bug recurred repeatedly.
 *
 * THE FIX: wrap the still image in an aspect-ratio-LOCKED letterboxed frame
 * (`min(100vw, 100vh*AR)` / `min(100vh, 100vw/AR)` — the standard
 * `object-fit:contain`-equivalent sizing formula using true viewport units,
 * never parent percentages). The image is rendered at exactly that frame's
 * size (never cropped), so ANY point measured as a PERCENT of the ORIGINAL
 * IMAGE lands at that same percent of the frame, on every window size. The
 * zoom (`scale`) and the DOM overlay (`screenBox`) are both children of that
 * SAME frame, so they stay glued together through every zoom level.
 *
 * `screenBox` MUST be measured directly off the still image (crop + eyeball
 * or a script), as a percent of the FULL image — see
 * `sim3d/ops/_measure_monitor.py` for the method. Never guess a vw/vh offset.
 */
export function CinemaScreen({
  imageUrl,
  aspect,
  originPct,
  scale,
  screenBox,
  children,
}: {
  imageUrl: string;
  /** width / height of the source still. */
  aspect: number;
  /** Zoom focus point, as a percent of the ORIGINAL image (0-100). */
  originPct: { x: number; y: number };
  /** Current cinematic zoom level (1 = establish / no zoom). */
  scale: number;
  /** The physical screen's bounding box, as a percent of the ORIGINAL image. */
  screenBox: { left: number; top: number; width: number; height: number };
  children?: ReactNode;
}) {
  return (
    <div
      className="cs-outer"
      style={{ "--cs-ar": aspect } as React.CSSProperties}
      aria-hidden={!children}
    >
      <div
        className="cs-frame"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: `${originPct.x}% ${originPct.y}%`,
        }}
      >
        <img className="cs-bg" src={imageUrl} alt="" draggable={false} />
        {children && (
          <div
            className="cs-screen"
            style={{
              left: `${screenBox.left}%`,
              top: `${screenBox.top}%`,
              width: `${screenBox.width}%`,
              height: `${screenBox.height}%`,
            }}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
